import { Box, Chip, Icon } from "@outschool/backpack";
import { faCalendar } from "@outschool/icons";
import * as Time from "@outschool/time";
import React, { useMemo } from "react";

import useSectionString from "../../../../shared/hooks/useSectionString";
import { useTimeZone } from "../../../providers/TimeZoneProvider";

type NextStartTimeProps = {
  nextSectionStartTime?: string;
  nextOngoingMeetingStartTime?: string;
};

/**
 * Calculates the next start time for a section. Null if there's no next start time we want to show.
 */
const calculateNextDate = (
  nextSectionStartTime: string | undefined,
  nextOngoingMeetingStartTime: string | undefined,
  userTimeZone: string,
): string | null => {
  if (!nextSectionStartTime && !nextOngoingMeetingStartTime) {
    return null;
  }

  // If nextSection is provided & not past, use that - else nextOngoingMeetingStartTime
  const nextDate =
    nextSectionStartTime &&
    Time.isDateInThePast(nextSectionStartTime, userTimeZone)
      ? nextOngoingMeetingStartTime
      : nextSectionStartTime || nextOngoingMeetingStartTime;

  return nextDate || null;
};

/**
 * Next start time banner for the search results gallery experiment.
 * Matches the mobile version design with a simple gray chip style.
 */
export function NextStartTime({
  nextSectionStartTime,
  nextOngoingMeetingStartTime,
}: NextStartTimeProps) {
  const { timeZone: userTimeZone } = useTimeZone();
  const { formatNextSessionStartTime } = useSectionString();

  const nextStartTimeString = useMemo(() => {
    const nextDate = calculateNextDate(
      nextSectionStartTime,
      nextOngoingMeetingStartTime,
      userTimeZone || "UTC",
    );
    return nextDate
      ? formatNextSessionStartTime(nextDate, userTimeZone || "UTC")
      : null;
  }, [
    nextSectionStartTime,
    nextOngoingMeetingStartTime,
    userTimeZone,
    formatNextSessionStartTime,
  ]);

  if (!nextStartTimeString) {
    return null;
  }

  return (
    <Chip
      size="small"
      label={
        <Box
          flex
          sx={{
            alignItems: "center",
            justifyContent: "flex-start",
            width: "100%",
            gap: "8px",
          }}
        >
          <Icon
            icon={faCalendar}
            sx={{
              fontSize: "16px",
              color: "grey.600",
            }}
          />
          <Box
            sx={{
              color: "grey.700",
              fontWeight: "500",
              fontSize: "14px",
            }}
          >
            {nextStartTimeString}
          </Box>
        </Box>
      }
      sx={{
        width: "100%",
        backgroundColor: "grey.50",
        color: "grey.700",
        justifyContent: "flex-start",
        paddingY: "12px",
        paddingX: "8px",
        "& .MuiChip-label": {
          width: "100%",
          textAlign: "left",
        },
      }}
    />
  );
}
